import {
  commonStyles,
  createStyles,
  type StylesClasses,
  STYLE_SIZE_CONSTANTS,
} from '@utils/styles'

const styles = createStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: {
      xs: '16px',
      sm: '32px',
    },
    width: '100%',
    alignItems: 'center',
    position: 'relative',
  },
  billingToggleWrapper: {
    padding: {
      xs: '4px 8px',
      sm: '8px 16px',
    },
    backgroundColor: 'background.default',
    boxShadow: '0px 4px 16px 4px rgba(0, 0, 0, 0.06)',
    display: 'flex',
    flexWrap: 'nowrap',
    width: 'fit-content',
    alignItems: 'center',
    borderRadius: '8px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.light',
    marginRight: 'auto',
  },
  billingButton: {
    border: 'none',
    padding: '5px 10px',
    borderRadius: '8px',
    minWidth: {
      xs: '80px',
      sm: '176px',
    },
    '&:hover': {
      border: 'none',
      backgroundColor: 'none !important',
    },
    textTransform: 'none',
  },
  annualBillingLayout: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    justifyContent: 'center',
  },
  annualChip: {
    borderRadius: '1260px',
    '& .MuiChip-label': {
      paddingInline: '0px !important',
    },
  },
  controlsWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    flexWrap: 'wrap',
    width: '100%',
  },
  currencySelect: {
    minWidth: '100px',
    height: '40px',
    '& .MuiSelect-select': {
      paddingTop: '8px',
      paddingBottom: '8px',
    },
  },
  cardsContainer: {
    display: 'flex',
    flexDirection: {
      xs: 'column',
      sm: 'row',
    },
    gap: '24px',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'stretch', // Ensure equal height
  },
  card: {
    padding: '32px 24px',
    borderRadius: '16px',
    border: '1px solid',
    borderColor: 'border.light',
    backgroundColor: 'background.paper',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: '280px',
    maxWidth: {
      xs: 'unset',
      sm: '380px',
    },
    minHeight: {
      xs: '332px',
      sm: 'unset',
    },
    position: 'relative',
    transition: 'all 0.3s ease',
    boxShadow: {
      xs: 2,
      sm: 'unset',
    }, // Blue halo effect
  },
  highlightedCard: {
    border: '2px solid',
    borderColor: 'primary.main',
    boxShadow: '0px 0px 6.53px 0px #4540D5', // Blue halo effect
    zIndex: 1,
    transform: 'scale(1.02)', // Slightly larger
  },
  recommendedChip: {
    borderRadius: '4px',
    fontWeight: 600,
    height: '24px',
    fontSize: '0.75rem',
  },

  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  checkIcon: {
    color: 'success.main',
    marginTop: '2px', // Align with text
    minWidth: '20px',
  },
  creditsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '4px',
  },
  creditIcon: {
    width: '16px',
    height: '16px',
    color: 'warning.main',
  },
  mobileCarouselWrapper: {
    display: {
      xs: 'block',
      md: 'none',
    },
    width: '100%',
  },
  desktopGridWrapper: {
    display: {
      xs: 'flex',
      md: 'flex',
    },
    width: '100%',
    justifyContent: 'center',
  },

  controlWrapper: {
    ...commonStyles.flexSpace(),
    width: '100%',
    alignItems: {
      xs: 'flex-end',
      sm: 'center',
    },
    flexDirection: 'row',
    rowGap: {
      xs: '1rem',
      sm: 'unset',
    },
  },

  comparePlansButton: {
    fontWeight: '500',
    alignSelf: 'center',
    display: {
      xs: 'none',
      sm: 'flex',
    },
  },

  // Mobile Tabs
  mobileTabsWrapper: {
    '& .MuiButton-root': {
      whiteSpace: 'nowrap',
      minWidth: 'fit-content',
      '&[data-selected="true"]': {
        backgroundColor: '#4540D51A',
        color: 'primary.main',
      },
    },
    minWidth: '100%',
    position: 'relative',
    display: {
      xs: 'flex',
      sm: 'none',
    },
    height: '40px',
  },

  activeMobileTab: {
    backgroundColor: 'primary.trans',
    borderColor: 'primary.main',
    color: 'primary.main',
  },

  // Mobile feature comparison table
  mobileFeatureTable: {
    display: { xs: 'flex', sm: 'none' },
    flexDirection: 'column',
    width: '100%',
    marginTop: '32px',
    border: '1px solid',
    borderColor: 'border.light',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  mobileCategoryHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid',
    borderColor: 'border.light',
  },
  mobileFeatureRow: {
    display: 'flex',
    flexDirection: 'column',
    borderBottom: '1px solid',
    borderColor: 'border.light',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  mobileFeatureName: {
    backgroundColor: 'custom.mobileFeatureNameBackground',
    padding: '8px 16px',
  },
  mobileValuesContainer: {
    display: 'flex',
    width: '100%',
    borderTop: '1px solid',
    borderColor: 'border.light',
  },
  mobileValueCell: {
    flex: 1,
    padding: '16px 8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    borderRight: '1px solid',
    borderColor: 'border.light',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  fixedChoosePlanButton: {
    display: { xs: 'flex', sm: 'none' },
    alignItems: 'center',
    position: 'fixed',
    bottom: '68px', // Adjusted for better spacing from screen edge
    zIndex: 100,
    width: '100dvw',
    backgroundColor: 'background.paper',
    left: '50%',
    transform: 'translateX(-50%) translateY(200%)',
    transition:
      'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), bottom 0.3s ease-in-out',
    padding: `12px ${STYLE_SIZE_CONSTANTS.sectionWrapperPadding.mobileLeftRightPadding}`,
  },
  fixedChoosePlanButtonVisible: {
    transform: 'translateX(-50%) translateY(0)',
  },
  stickyControl: {
    position: {
      xs: 'sticky',
      sm: 'unset',
    },
    top: STYLE_SIZE_CONSTANTS.headerHeight,
    zIndex: 10,
    backgroundColor: 'white',
    padding: {
      xs: `24px ${STYLE_SIZE_CONSTANTS.sectionWrapperPadding.mobileLeftRightPadding}`,
      sm: 'unset',
    },
    paddingTop: {
      xs: '10px',
      sm: 'unset',
    },
    width: {
      xs: '100dvw',
      sm: '100%',
    },
  },
})

export default styles
export type StylesClassNames = StylesClasses<typeof styles>
